import * as z from 'zod'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { FormDirtyIndicator } from '../components/form-dirty-indicator'
import { FormNavigationGuard } from '../components/form-navigation-guard'
import { SettingsSection } from '../components/settings-section'
import { SettingsCard } from '../components/settings-card'
import { useSettingsForm } from '../hooks/use-settings-form'
import { useUpdateOption } from '../hooks/use-update-option'

interface StorageConfigDefaults {
  DBHost: string
  DBPort: string
  DBUser: string
  DBPassword: string
  DBName: string
  COSBucket: string
  COSAppId: string
  COSRegion: string
  COSSecretId: string
  COSSecretKey: string
}

const storageSchema = z.object({
  DBHost: z.string(),
  DBPort: z.string(),
  DBUser: z.string(),
  DBPassword: z.string(),
  DBName: z.string(),
  COSBucket: z.string(),
  COSAppId: z.string(),
  COSRegion: z.string(),
  COSSecretId: z.string(),
  COSSecretKey: z.string(),
})

type StorageFormValues = z.infer<typeof storageSchema>

function normalizeValue(value: unknown): string {
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') return String(value)
  return String(value ?? '')
}

export function StorageConfigSection({
  defaultValues,
}: {
  defaultValues: StorageConfigDefaults
}) {
  const updateOption = useUpdateOption()
  const { t } = useTranslation()

  const {
    form,
    handleReset,
    handleSubmit,
    isDirty,
    isSubmitting,
  } = useSettingsForm<StorageFormValues>({
    resolver: zodResolver(storageSchema) as Resolver<StorageFormValues>,
    defaultValues,
    onSubmit: async (_data, changedFields) => {
      for (const [key, value] of Object.entries(changedFields)) {
        await updateOption.mutateAsync({
          key,
          value: normalizeValue(value),
        })
      }
    },
  })

  return (
    <div className='space-y-6'>
      <FormNavigationGuard when={isDirty} />
      <SettingsSection
        title='存储配置'
        description='配置数据库连接和对象存储（COS）参数'
      >
        <Form {...form}>
          <form onSubmit={handleSubmit} className='space-y-6'>
        {/* 数据库配置 */}
        <SettingsCard
          title='数据库配置'
          description='MySQL / CynosDB 连接参数'
        >
          <div className='grid gap-4 sm:grid-cols-2'>
            <FormField
              control={form.control}
              name='DBHost'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>数据库地址</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='gz-cynosdbmysql-grp-xxx.sql.tencentcdb.com' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='DBPort'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>数据库端口</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='3306' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className='mt-4 grid gap-4 sm:grid-cols-2'>
            <FormField
              control={form.control}
              name='DBUser'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>数据库账号</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='root' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='DBPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>数据库密码</FormLabel>
                  <FormControl>
                    <Input {...field} type='password' placeholder='••••••••' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className='mt-4'>
            <FormField
              control={form.control}
              name='DBName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>数据库名称</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='new_api' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </SettingsCard>

        {/* 对象存储配置 */}
        <SettingsCard
          title='对象存储配置'
          description='腾讯云 COS (Cloud Object Storage) 参数'
        >
          <div className='grid gap-4 sm:grid-cols-2'>
            <FormField
              control={form.control}
              name='COSBucket'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bucket 名称</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='kaisen01' />
                  </FormControl>
                  <FormDescription>
                    存储桶名称（不含 AppId）
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='COSAppId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>AppId</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='1301578313' />
                  </FormControl>
                  <FormDescription>
                    腾讯云账号应用 ID
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className='mt-4'>
            <FormField
              control={form.control}
              name='COSRegion'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>地域 (Region)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='ap-guangzhou' />
                  </FormControl>
                  <FormDescription>
                    例如 ap-guangzhou（广州）、ap-shanghai（上海）
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className='mt-4 grid gap-4 sm:grid-cols-2'>
            <FormField
              control={form.control}
              name='COSSecretId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SecretId</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder='AKIDxxxxxxxxxxxxxxxxxxxxxxxxxxxx' />
                  </FormControl>
                  <FormDescription>
                    腾讯云 API 密钥 SecretId
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='COSSecretKey'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SecretKey</FormLabel>
                  <FormControl>
                    <Input {...field} type='password' placeholder='••••••••' />
                  </FormControl>
                  <FormDescription>
                    腾讯云 API 密钥 SecretKey
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </SettingsCard>

        {/* 操作按钮 */}
        <div className='flex items-center gap-3'>
          <Button
            type='submit'
            disabled={!isDirty || isSubmitting}
            size='sm'
          >
            <FormDirtyIndicator isDirty={isDirty} />
            {isSubmitting ? '保存中...' : '保存配置'}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={handleReset}
            disabled={!isDirty}
          >
            <RotateCcw className='mr-1.5 size-3.5' />
            重置
          </Button>
        </div>
          </form>
        </Form>
      </SettingsSection>
    </div>
  )
}
